import { businessPeriod, clockView, serverTime } from '../../../core/time/business-time';
import { BusinessClock } from '../../../core/time/business-clock.service';
import { Injectable, inject } from '@angular/core';
import { RegistrationApi, RegistrationRequestError } from '../data-access/registration-api.service';
import { RegistrationKind, RegistrationOptions, registrationPayload } from '../domain/registration-command';
import { Step4CompletionState } from '../domain/registration-model';
import { RegistrationImages } from '../integrations/registration-images.service';
import { RegistrationReceiptStorage } from '../integrations/registration-receipt-storage';
import { MemberStateService } from './member-state.service';

export type RegistrationNavigation = { type: RegistrationKind; studioId: number };
export type SubmitResult = { kind: 'result'; destination: RegistrationNavigation } | { kind: 'expired' } | { kind: 'blocked' };

@Injectable({ providedIn: 'root' })
export class RegistrationWorkflow {
  private readonly clock = inject(BusinessClock);
  private readonly state = inject(MemberStateService);
  private readonly api = inject(RegistrationApi);
  private readonly images = inject(RegistrationImages);
  private readonly receipts = inject(RegistrationReceiptStorage);
  private receipt: Step4CompletionState | null = null;
  private running = false;
  message = '';
  get busy(): boolean { return this.running; }
  get unknown(): boolean { return this.receipt?.status === 'pending' || this.receipt?.status === 'unknown'; }
  get imageWarning(): boolean { return this.receipt?.imageWarning === true; }

  resume(): RegistrationNavigation | null {
    if (this.running) return this.receipt ? this.destination(this.receipt) : null;
    try { this.receipt = this.receipts.read(); }
    catch {
      this.message = '登録結果の記録を確認できません。再申込せず店舗へお問い合わせください。';
      return null;
    }
    if (!this.receipt) { this.message = ''; return null; }
    this.applyReceipt(this.receipt);
    return this.destination(this.receipt);
  }
  private destination(receipt: Step4CompletionState): RegistrationNavigation {
    return { type: receipt.type as RegistrationKind, studioId: Number(receipt.studioId) };
  }
  private applyReceipt(receipt: Step4CompletionState): void {
    const view = this.state.get();
    view.showConfirm = false; view.showResult = true; view.showProcess = this.running;
    view.showSuccess = receipt.status === 'success'; view.showFailed = receipt.status === 'failed';
    view.gmoCreateError = receipt.gmoCreateError; view.emailSent = receipt.emailSent;
    view.member.studioId = Number(receipt.studioId); view.member.id = receipt.memberId;
    view.member.payment.paymentMethod = receipt.paymentMethod;
    view.member.btPaymentEnabled = receipt.btPaymentEnabled;
    this.state.setLang(receipt.lang);
    this.state.completedRegistration = receipt.status === 'success' ? this.destination(receipt) : null;
    this.message = this.unknown ? 'お申し込みの結果を確認できません。登録済みの可能性があります。再申込せず店舗へ登録状況をお問い合わせください。' : '';
  }
  /** Explicit confirmation of an external check, never an automatic resend. */
  acknowledgeUnknown(): boolean {
    if (!this.unknown || this.running) return false;
    try { this.receipts.clear(); } catch { return false; }
    this.receipt = null; this.message = ''; this.state.reset(); return true;
  }
  beginNew(): boolean {
    this.resume();
    if (this.running || this.unknown || this.message) return false;
    this.receipts.clear(); this.receipt = null; this.state.reset(); return true;
  }

  /** A confirmed questionnaire can advance to that same member's main registration. */
  beginMainRegistration(memberId: number, studioId: number): boolean {
    this.resume();
    if (this.running || this.message || this.receipt?.status !== 'success' ||
        this.receipt.type !== 'temporary_registration' ||
        Number(this.receipt.memberId) !== memberId || Number(this.receipt.studioId) !== studioId) return false;
    return this.beginNew();
  }

  async submit(kind: RegistrationKind, options: RegistrationOptions, files: readonly (File | null | undefined)[]): Promise<SubmitResult> {
    if (this.running) return { kind: 'blocked' };
    const previous = this.resume();
    if (previous && (this.unknown || this.receipt?.status === 'success')) return { kind: 'result', destination: previous };
    if (this.message) return { kind: 'blocked' };
    const source = this.state.get();
    if (!source.member.studioId) return { kind: 'blocked' };
    const draft = source.member;
    const flowId = this.state.flowId;
    const timeView = { ...clockView(false), period: options.clockPeriod ?? businessPeriod() };
    const studioId = draft.studioId;
    const current = () => source === this.state.get() && source.member === draft && this.state.flowId === flowId && draft.studioId === studioId;
    const payload = registrationPayload(source.member, kind, options);
    let password = source.member.tmp.tempPass;
    let imageKey: string | undefined;
    let prepared: Array<{ index: number; file: File }> = [];
    let sent = false;
    this.running = true; this.message = '';
    source.showProcess = true;
    const receipt: Step4CompletionState = {
      flowId, operationId: crypto.randomUUID(),
      type: kind, studioId: String(source.member.studioId), status: 'pending', savedAt: serverTime.now(), clockBasis: 'server-jst-v1',
      gmoCreateError: false, emailSent: false, lang: source.member.translate ?? source.lang,
      paymentMethod: source.member.payment.paymentMethod, btPaymentEnabled: source.member.btPaymentEnabled,
    };
    try {
      prepared = await this.images.prepare(kind === 'new_membership' ? files : []);
      if (!await this.clock.confirm(timeView)) {
        this.message = this.clock.message();
        return { kind: 'blocked' };
      }
      receipt.savedAt = serverTime.now();
      this.receipts.write(receipt); this.receipt = receipt;
      source.showConfirm = false; source.showResult = true; source.showSuccess = false; source.showFailed = false;
      sent = true;
      const result = await this.api.register(kind, payload, password);
      imageKey = result.imageKey;
      receipt.status = 'success'; receipt.memberId = result.memberId;
      receipt.gmoCreateError = result.gmoCreateError; receipt.emailSent = result.emailSent;
      // Persist confirmed creation before optional attachment work. Reload must not repeat creation.
      receipt.imageWarning = prepared.length > 0;
      try { this.receipts.write(receipt); } catch { /* Existing pending record still prevents retry after reload. */ }
      if (current()) this.state.clearReservations();
      receipt.imageWarning = !(await this.images.upload(result.memberId, imageKey, prepared));
      try { this.receipts.write(receipt); } catch { /* Retain confirmed/pending evidence already stored. */ }
      if (kind === 'main_registration') void this.api.deleteExpiredPassword().catch(() => undefined);
      return { kind: 'result', destination: this.destination(receipt) };
    } catch (error) {
      if (receipt.status === 'success') {
        receipt.imageWarning = prepared.length > 0;
        try { this.receipts.write(receipt); } catch { /* Earlier confirmed evidence remains. */ }
        return { kind: 'result', destination: this.destination(receipt) };
      }
      if (sent && error instanceof RegistrationRequestError && error.reason === 'expired-reservation') {
        try { this.receipts.clear(); } catch {
          receipt.status = 'unknown';
          return { kind: 'result', destination: this.destination(receipt) };
        }
        this.receipt = null;
        if (current()) {
          const ids = this.state.clearReservations();
          try { await this.api.clearExpiredReservations(ids); } catch { /* Expired reservations cannot be reused. */ }
        }
        source.showConfirm = true; source.showResult = false;
        return { kind: 'expired' };
      }
      if (!sent) {
        this.message = '登録の準備に失敗しました。画像またはブラウザの保存領域を確認して、もう一度お試しください。';
        source.showConfirm = true; source.showResult = false;
        return { kind: 'blocked' };
      }
      receipt.status = 'unknown';
      try { this.receipts.write(receipt); } catch { /* The pending marker remains sufficient to block retry. */ }
      return { kind: 'result', destination: this.destination(receipt) };
    } finally {
      this.running = false;
      source.showProcess = false;
      if (sent) {
        draft.cc = {}; delete draft.tmp.tempPass;
        if (current()) source.uploadImg = {};
        delete payload['cc']; password = undefined; imageKey = undefined; prepared = [];
      }
      if (current() && this.receipt) this.applyReceipt(this.receipt);
    }
  }
}
